import {
  CLIENT_RECORD_SOURCES,
  clientRecordConditionValue,
  QUIZ_RESULT_SOURCE_PREFIX,
  QUIZ_SCORE_SOURCE_PREFIX,
} from "@repo/shared";
import type {
  LocalizedString,
  PublicFlowDefinitionExtras,
  PublicFlowInstanceResponse,
} from "@repo/shared/types";
import { isFlowInstanceEditable, suitabilityClientType } from "@repo/shared/types";
import { useEffect, useMemo, useState } from "react";
import { getWithToken } from "@/lib/api/_client";
import { useSessionToken } from "@/lib/context/public-session-context";
import { usePublicClientClassificationValue } from "./use-client-classification";
import { usePublicFieldValues } from "./use-custom-field-values";
import {
  usePublicFlowClient,
  usePublicFlowDefinition,
  usePublicFlowTenant,
} from "./use-flow-instance";
import { usePublicGoals } from "./use-goals";
import { usePublicProductAssessmentConditionValues } from "./use-product-assessment";
import { usePublicQuizConditionValues, usePublicQuizzes } from "./use-quiz";
import { usePublicSumsubVerificationValue } from "./use-sumsub-verification";

type ProductTypeSummary = { id: string; nameI18n: LocalizedString };

/**
 * Shared data layer for the public flow (§3.11). Bundles the per-resource reads
 * (definition / client / tenant / goals), the product-types fetch, and the
 * reactive `formValues` memo so the shell + both mode bodies read from one
 * source instead of duplicating it.
 *
 * Must be called inside both `PublicSessionProvider` (reads the token via
 * `useSessionToken()`, which throws outside it) and `PublicFlowProvider` (uses
 * the optimistic form-value store via the imported value hooks / store hook in
 * the shell).
 */
export function usePublicFlowData(
  flowInstance: PublicFlowInstanceResponse,
  storeFormValues: Record<string, string>,
) {
  const sessionToken = useSessionToken();
  const { definition } = usePublicFlowDefinition();
  const { client } = usePublicFlowClient();
  const { tenant } = usePublicFlowTenant();
  const { goals } = usePublicGoals();

  // Product types — fetched off the definition's collection id, gated behind
  // `ready` so renderers always see a populated list.
  const [productTypes, setProductTypes] = useState<ProductTypeSummary[]>([]);
  const [productTypesLoaded, setProductTypesLoaded] = useState(false);
  const portfolioCollectionId = (definition as any)?.portfolioCollectionId as
    | string
    | undefined
    | null;
  useEffect(() => {
    if (!portfolioCollectionId) {
      setProductTypesLoaded(true);
      return;
    }
    let cancelled = false;
    getWithToken<ProductTypeSummary[]>(
      `/public/flow/snapshots/collections/${portfolioCollectionId}/product-types`,
      sessionToken,
    )
      .then((result) => {
        if (cancelled) return;
        setProductTypes(result || []);
        setProductTypesLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setProductTypesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [portfolioCollectionId, sessionToken]);

  // ── Reactive conditional-visibility / form values ──
  const { fieldValues: customFieldValues } = usePublicFieldValues();
  const { calculatedData: classificationCalculatedData } = usePublicClientClassificationValue();
  const { calculatedData: sumsubCalculatedData } = usePublicSumsubVerificationValue();
  // QL-13 — quiz results feed `__quiz_result_<id>` conditional rules.
  const { quizzes } = usePublicQuizzes();
  // QL-361 / QL-698 — `__quiz_score_<id>` values, resolved on the server so a
  // member's session reads the whole group's score range.
  const quizConditionValues = usePublicQuizConditionValues();
  // QL-361 / QL-698 — product-assessment outcomes feed `__pa_…` conditional
  // rules, resolved on the server so a member's session reads the whole group.
  const productAssessmentConditionValues = usePublicProductAssessmentConditionValues();
  const clientCountry = client?.country;
  const clientRecordType = client?.type;
  // QPBI-395 — client custom-field values referenced by the flow's public
  // conditional rules (the backend ships only those).
  const conditionCustomValues = client?.conditionCustomValues;

  const formValues = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};

    for (const cfv of customFieldValues) {
      if (!cfv.flowFieldId) continue;
      // Goal-scoped values keyed `${goalId}:${fieldId}` so conditional visibility
      // can resolve them per goal; instance values keyed `${fieldId}`.
      if (cfv.goalId) {
        map[`${cfv.goalId}:${cfv.flowFieldId}`] = cfv.value;
      } else {
        map[cfv.flowFieldId] = cfv.value;
      }
    }

    if (classificationCalculatedData) {
      try {
        const d = JSON.parse(classificationCalculatedData);
        if (d.classificationType) map.__classification_type__ = d.classificationType;
      } catch {
        /* ignore malformed blob */
      }
    }
    if (sumsubCalculatedData) {
      try {
        const d = JSON.parse(sumsubCalculatedData);
        if (d.hasBeenChecked) {
          map.__sumsub_verified__ = d.status?.isVerified ? "yes" : "no";
          // QL-68 — alpha-3 issuing country of the verified document.
          if (typeof d.info?.country === "string" && d.info.country) {
            map.__sumsub_document_country__ = d.info.country;
          }
        }
      } catch {
        /* ignore */
      }
    }
    // Trapets virtual fields intentionally NOT derived here — advisor-only.

    // QL-217 — client type and country under their sentinels. The evaluator's
    // formValues fallback reads them; before, `__client_type__` read "" here
    // and hid blocks and tabs the completion engine still required.
    for (const source of CLIENT_RECORD_SOURCES) {
      map[source] =
        clientRecordConditionValue(source, { type: clientRecordType, country: clientCountry }) ??
        "";
    }

    // QPBI-395 — custom-field values under their full `__custom_field_<id>`
    // sentinel; the evaluator's formValues fallback resolves them like
    // `__client_country__` above. No collision with flow-field keys (UUIDs).
    for (const cv of conditionCustomValues ?? []) {
      map[`__custom_field_${cv.customFieldId}`] = cv.value;
    }

    // QL-13 — one entry per quiz on this flow, carrying the client's status.
    for (const entry of quizzes ?? []) {
      map[`${QUIZ_RESULT_SOURCE_PREFIX}${entry.quiz.id}`] = entry.status;
      // QL-361 — seeded "" when there is no score, which never matches.
      // QL-698 — resolved on the server (a member session's own value blob has
      // the other members' scores stripped).
      const scoreSource = `${QUIZ_SCORE_SOURCE_PREFIX}${entry.quiz.id}`;
      map[scoreSource] = quizConditionValues?.[scoreSource] ?? "";
    }

    // QL-361 — one entry per CONFIGURED product-assessment source, seeded even
    // when nothing is stored so "not answered" rules match. A source that is
    // not configured gets no entry, which the evaluator reads as "never matches".
    // QL-698 — resolved on the server from the full record (a member session's
    // own value blob has the other members' scores stripped).
    Object.assign(map, productAssessmentConditionValues ?? {});

    // Optimistic overrides applied LAST so an in-flight keystroke wins.
    return { ...map, ...storeFormValues };
  }, [
    customFieldValues,
    classificationCalculatedData,
    sumsubCalculatedData,
    clientCountry,
    clientRecordType,
    conditionCustomValues,
    quizzes,
    quizConditionValues,
    productAssessmentConditionValues,
    storeFormValues,
  ]);

  // QPBI-395 — field id → type, so conditional rules compare values the way
  // the control renders them. Client custom fields are keyed by their full
  // `__custom_field_<id>` sentinel; virtual sentinel keys are deliberately
  // absent and keep the lenient string semantics.
  const fieldTypes = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const f of ((definition as any)?.fields ?? []) as Array<{ id: string; type: string }>) {
      map[f.id] = f.type;
    }
    for (const cv of conditionCustomValues ?? []) {
      map[`__custom_field_${cv.customFieldId}`] = cv.type;
    }
    return map;
  }, [definition, conditionCustomValues]);

  const flowLang = ((definition as any)?.language || "en_GB") as string;
  const allPages = ((definition as any)?.pages ?? []) as Array<any>;
  const isMultiGoal = Boolean((definition as any)?.multiGoal);
  // QL-682 — false when the public pages are all global: no goal navigation.
  const hasGoalPages =
    (definition as Partial<PublicFlowDefinitionExtras> | undefined)?.hasGoalPages ?? true;
  // Locked = any non-editable status (submitted / process / completed / cancelled),
  // matching the backend `isFlowInstanceEditable` + the advisor wizard. Drives the
  // read-only UI: no Submit, no progress bar, disabled widgets. (A submitted public
  // instance is NOT "completed" but IS locked — `status === "completed"` was wrong.)
  const isCompleted = !isFlowInstanceEditable(flowInstance.status);
  const chartColors = (tenant as any)?.chartColors;
  const portfolioCosts = (definition as any)?.portfolioCollection?.portfolioCosts || [];
  // Group is kept: the QL-188 group notes need it. Components that only know
  // Person/Company (client classification, QP-184) fold it with
  // `suitabilityClientType` where they render.
  const rawClientType = (client as { type?: string } | null)?.type;
  const clientType: "Person" | "Company" | "Group" =
    rawClientType === "Group" ? "Group" : suitabilityClientType(rawClientType);

  const ready = Boolean(definition && client && tenant && productTypesLoaded);

  return {
    definition,
    client,
    tenant,
    goals,
    productTypes,
    formValues,
    fieldTypes,
    flowLang,
    allPages,
    isMultiGoal,
    hasGoalPages,
    isCompleted,
    chartColors,
    portfolioCosts,
    clientType,
    ready,
  };
}
